import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay, catchError, map, forkJoin } from 'rxjs';
import { environment } from '@root/src/environments/environment';

export interface FrontendVersionInfo {
  version: string;
  commitSha: string;
  branch: string;
  deployDateTime: string;
  buildNumber: string;
  environment: string;
}

export interface BackendVersionInfo {
  version?: string | null;
  commitSha: string;
  deployDateTime: string;
  branch: string;
  serverTime: string;
}

export interface CombinedVersionInfo {
  frontend: FrontendVersionInfo | null;
  backend: BackendVersionInfo | null;
}

@Injectable({
  providedIn: 'root'
})
export class VersionInfoService {
  private http = inject(HttpClient);
  private cachedCombinedInfo: CombinedVersionInfo | null = null;
  private loading$: Observable<CombinedVersionInfo | null> | null = null;

  getCombinedVersionInfo(): Observable<CombinedVersionInfo | null> {
    if (this.cachedCombinedInfo) {
      return of(this.cachedCombinedInfo);
    }
    
    if (this.loading$) {
      return this.loading$;
    }
    
    this.loading$ = forkJoin({
      frontend: this.getFrontendVersion().pipe(catchError(() => of(null))),
      backend: this.getBackendVersion().pipe(catchError(() => of(null)))
    }).pipe(
      map(result => {
        this.cachedCombinedInfo = result;
        this.loading$ = null;
        return result;
      }),
      shareReplay(1)
    );
    
    return this.loading$;
  }

  private getFrontendVersion(): Observable<FrontendVersionInfo | null> {
    return this.http.get<FrontendVersionInfo>('/assets/version.json').pipe(
      catchError((error) => {
        console.error('Failed to load frontend version info:', error);
        return of(null);
      })
    );
  }

  private getBackendVersion(): Observable<BackendVersionInfo | null> {
    const backendUrl = environment.versionApi?.backendUrl || '/api/version';
    console.log('Loading backend version from:', backendUrl);
    
    return this.http.get<BackendVersionInfo>(backendUrl).pipe(
      map(response => {
        console.log('Backend version loaded:', response);
        return response;
      }),
      catchError((error) => {
        console.error('Failed to load backend version info:', error);
        return of(null);
      })
    );
  }

  formatSha(sha: string): string {
    if (!sha || sha === 'development') return 'N/A';
    return sha.length > 7 ? sha.substring(0, 7) : sha;
  }

  formatDeployDate(dateTime: string): string {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    return date.toLocaleString();
  }

  getRelativeTime(dateTime: string): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }
}